import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/components/BrandProvider";
import { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

// Fix K (2026-09-22): la cuenta de administradora NUNCA se publica en el login ni en el bundle.
// Solo quedan las dos cuentas demo, que se desactivan solas al apagar los datos de demostración.
const DEMO = [
  { rol: "Operador", email: "operador@marthitashop.do", pass: "operador123" },
  { rol: "Contadora", email: "contadora@marthitashop.do", pass: "contadora123" },
];

export default function Login() {
  const { login } = useAuth();
  const marca = useBrand() || {};
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [olvidoOpen, setOlvidoOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      nav("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No pudimos iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground ms-grain overflow-hidden">
        {/* Fix K (2026-09-22): el logo principal va sobre tarjeta blanca (es naranja sobre
            transparente: directo sobre el panel naranja sería invisible). Respaldo: ícono + nombre. */}
        {marca.logo_url ? (
          <div className="relative z-10 self-start rounded-2xl bg-white px-5 py-3 shadow-sm" data-testid="login-logo-principal">
            <img src={`${BACKEND_URL}${marca.logo_url}`} alt={marca.nombre_comercial || "Logo"} className="h-20 w-auto max-w-[260px] object-contain" />
          </div>
        ) : (
          <div className="flex items-center gap-3 relative z-10">
            {marca.logo_icono_url ? (
              <img src={`${BACKEND_URL}${marca.logo_icono_url}`} alt="" className="h-11 w-11 rounded-2xl object-contain bg-white/10" />
            ) : (
              <div className="h-11 w-11 rounded-2xl bg-white/20 flex items-center justify-center">
                <Sparkles className="h-6 w-6" strokeWidth={1.5} />
              </div>
            )}
            <span className="font-head text-2xl font-semibold">{marca.nombre_comercial || "Marthitashop"}</span>
          </div>
        )}
        <div className="relative z-10 max-w-md">
          <h2 className="font-head text-4xl font-semibold leading-tight">
            {marca.frase_login || "Tu tienda de belleza, atendida 24/7."}
          </h2>
          <p className="mt-4 text-primary-foreground/80">
            {marca.subtitulo_login || "Panel de control con inventario centralizado y un agente de ventas con IA que conversa, sugiere y arma pedidos por vos."}
          </p>
        </div>
        <p className="relative z-10 text-primary-foreground/60 text-sm">República Dominicana · RD$</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            {marca.logo_icono_url ? (
              <img src={`${BACKEND_URL}${marca.logo_icono_url}`} alt="" className="h-10 w-10 rounded-2xl object-contain bg-primary/10" />
            ) : (
              <div className="h-10 w-10 rounded-2xl bg-primary flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-primary-foreground" strokeWidth={1.5} />
              </div>
            )}
            <span className="font-head text-2xl font-semibold">{marca.nombre_comercial || "Marthitashop"}</span>
          </div>
          <h1 className="font-head text-2xl font-semibold">Iniciar sesión</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Entrá con tu cuenta del equipo.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Correo</Label>
              <Input id="email" data-testid="login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="tucorreo@marthitashop.do" required className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-1.5 rounded-xl" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full rounded-full h-11 bg-primary hover:bg-primary/90 active:scale-[0.98] transition-transform">
              {loading ? "Entrando…" : "Entrar"}
            </Button>
            <button type="button" onClick={() => setOlvidoOpen(true)} data-testid="olvidaste-password-link"
              className="w-full text-center text-sm text-primary underline-offset-2 hover:underline">
              ¿Olvidaste tu contraseña?
            </button>
          </form>

          {marca.mostrar_cuentas_demo && (
            <div className="mt-8">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Cuentas demo (clic para llenar)</p>
              <div className="space-y-2">
                {DEMO.map((d) => (
                  <button key={d.email} type="button" data-testid={`demo-${d.rol}`}
                    onClick={() => { setEmail(d.email); setPassword(d.pass); }}
                    className="w-full text-left ms-card px-3 py-2.5 ms-lift">
                    <p className="text-sm font-semibold">{d.rol}</p>
                    <p className="text-xs text-muted-foreground">{d.email} · {d.pass}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center mt-8" data-testid="login-footer-cuentas">
            Las cuentas del equipo las crea la dueña desde Configuración → Usuarios. No hay registro público.
          </p>
          {/* Fix O (2026-09-24): enlace a la política de privacidad pública (la pide Meta) */}
          <p className="text-xs text-center mt-3">
            <a href={`${BACKEND_URL}/api/public/privacidad`} target="_blank" rel="noopener noreferrer"
              className="text-muted-foreground underline-offset-2 hover:underline" data-testid="login-link-privacidad">
              Política de privacidad
            </a>
          </p>
        </div>
      </div>

      <Dialog open={olvidoOpen} onOpenChange={setOlvidoOpen}>
        <DialogContent className="max-w-sm" data-testid="olvidaste-password-modal">
          <DialogHeader><DialogTitle>¿Olvidaste tu contraseña?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Por seguridad, las contraseñas las restablece la dueña desde Configuración → Usuarios.
            Pedile que te genere una nueva y vas a poder entrar enseguida.
          </p>
          {marca.correo_contacto && (
            <p className="text-sm font-medium">Contacto: {marca.correo_contacto}</p>
          )}
          <DialogFooter><Button className="rounded-full bg-primary hover:bg-primary/90" onClick={() => setOlvidoOpen(false)}>Entendido</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
